import React from 'react';

/* Content blocks for the Future AI Agent Pathway preview (edition ROLE_DEPTH_V3).
   Same visual language as the existing Section/Bullets; conditional labels stay visible. */
const KEY_HIDE = /(sourceIds|sectionId|Ref$|status$|Status$|labelType|contentVersion|attribution|referenceImage|assessedOn|heading|^label$)/;
const label = (k) => k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

export const Pill = ({ children, tone = 'amber', testId }) => {
  const tones = { amber: 'bg-amber-100 text-amber-900 border-amber-200', gray: 'bg-gray-100 text-gray-700 border-gray-200', teal: 'bg-teal-50 text-teal-800 border-teal-200' };
  return <span className={`inline-block text-[11px] uppercase tracking-widest font-extrabold px-2.5 py-1 rounded-full border ${tones[tone]}`} data-testid={testId}>{children}</span>;
};

const renderRich = (value, path = 'v') => {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return <ul className="space-y-2">{value.map((v, i) => <li key={i} className="flex gap-2.5 text-[15px] text-gray-800 leading-relaxed"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" /><div className="flex-1 min-w-0">{renderRich(v, `${path}-${i}`)}</div></li>)}</ul>;
  if (typeof value === 'object') return <div className="space-y-3">{Object.entries(value).filter(([k, v]) => !KEY_HIDE.test(k) && v != null && v !== '').map(([k, v]) => <div key={k}><p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-800 mb-1">{label(k)}</p>{renderRich(v, `${path}-${k}`)}</div>)}</div>;
  return <p className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-line">{String(value)}</p>;
};
export const Rich = ({ value, path }) => renderRich(value, path);

export const Expand = ({ title, children, testId }) => (
  <details className="mt-4 bg-white rounded-2xl border border-gray-200 px-4 py-3" data-testid={testId}>
    <summary className="cursor-pointer font-bold text-gray-900 min-h-11 flex items-center">{title}</summary>
    <div className="pt-3 border-t border-gray-100 mt-2">{children}</div>
  </details>
);

export const AgentRealityOpening = ({ data, guide }) => {
  if (!data) return null;
  const detail = ['digitalWorkflow', 'scopeBoundary', 'whatThisDoesNotEstablish', 'whyItIncludesSeniorWork', 'practicalNextStep', 'criticalFailureToTest'];
  return <div data-testid="ai-agent-reality">
    <div className="flex flex-wrap gap-2 mb-3">
      <Pill testId="ai-agent-reality-label">{data.displayLabel}</Pill>
      {data.labelType && <Pill tone="gray" testId="ai-agent-reality-label-type">{data.labelType}</Pill>}
    </div>
    <p className="text-[15px] md:text-base text-gray-900 leading-relaxed font-medium" data-testid="ai-agent-reality-answer">{data.directAnswer}</p>
    <Expand title="Read the full assessment" testId="ai-agent-reality-detail"><Rich value={Object.fromEntries(detail.filter((k) => data[k]).map((k) => [k, data[k]]))} /></Expand>
    {guide && <Expand title={guide.heading || 'AI, AGI and ASI — what the evidence supports'} testId="ai-concept-guide"><Rich value={guide} /></Expand>}
  </div>;
};

export const LearningExtras = ({ role, indiaGuide }) => {
  const ws = role.workSampleAcceptance; const cc = role.clarityCheck;
  return <div className="mt-6 space-y-4" data-testid="ai-learning-extras">
    {role.entryPreparation && <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl px-4 py-4" data-testid="ai-entry-preparation"><p className="text-[11px] uppercase tracking-widest font-extrabold text-indigo-800 mb-1.5">Entry preparation</p><p className="text-[15px] text-gray-800 leading-relaxed">{role.entryPreparation}</p></div>}
    {ws && <div className="bg-white border border-gray-200 rounded-2xl px-4 py-4" data-testid="ai-work-sample">
      <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-800 mb-1.5">Practical checks</p>
      {ws.status && <p className="text-xs text-gray-500 italic mb-2">{ws.status}</p>}
      {ws.exercise && <p className="text-[15px] text-gray-900 font-semibold mb-2">Exercise: {ws.exercise}</p>}
      <Rich value={ws.checks} />
      {ws.badgeRuleChange && <p className="text-xs text-gray-500 mt-2">{String(ws.badgeRuleChange)}</p>}
    </div>}
    {cc && <div className="bg-amber-50/70 border border-amber-200 rounded-2xl px-4 py-4" data-testid="ai-clarity-check">
      <p className="text-[11px] uppercase tracking-widest font-extrabold text-amber-900 mb-1.5">{cc.heading || 'Reflection'} · unscored</p>
      <p className="text-[15px] text-gray-900 font-semibold mb-2" data-testid="ai-clarity-question">{cc.question}</p>
      <Expand title="Answer guide" testId="ai-clarity-guide"><p className="text-[15px] text-gray-800 leading-relaxed">{cc.answerGuide}</p></Expand>
      {cc.purpose && <p className="text-xs text-gray-500 mt-2 italic">{cc.purpose}</p>}
    </div>}
    {indiaGuide && <Expand title={indiaGuide.heading || 'Preparing from India'} testId="ai-india-guide"><Rich value={indiaGuide} /></Expand>}
  </div>;
};

export const FutureOutlook = ({ data, sector }) => {
  if (!data) return null;
  const rest = ['taskExposure', 'exposedWork', 'criticalFailureExample', 'contractionTrigger', 'expansionCondition', 'evidenceThatWouldChangeThisAssessment', 'confidence', 'scopeBoundary'];
  return <div data-testid="ai-outlook-2035">
    <div className="flex flex-wrap gap-2 mb-3">
      <Pill testId="ai-outlook-status">{data.status || 'Conditional scenario'}</Pill>
      {data.assessedOn && <Pill tone="gray">Assessed {data.assessedOn}</Pill>}
    </div>
    <p className="text-[15px] md:text-base text-gray-900 leading-relaxed font-medium" data-testid="ai-outlook-answer">{data.directAnswer}</p>
    {Array.isArray(data.horizons) && <div className="mt-4 space-y-3">{data.horizons.map((h, i) => <div key={i} className="bg-white border border-gray-200 rounded-2xl px-4 py-4" data-testid={`ai-outlook-window-${i}`}>
      <p className="text-[11px] uppercase tracking-widest font-extrabold text-purple-800 mb-1.5">Scenario window {h.period}</p>
      <p className="text-[15px] text-gray-800 leading-relaxed">{h.scenario}</p>
      {h.learnerResponse && <p className="text-sm text-gray-700 mt-2"><span className="font-bold">Learner response:</span> {h.learnerResponse}</p>}
    </div>)}</div>}
    <Expand title="Exposure, triggers and what would change this assessment" testId="ai-outlook-detail"><Rich value={Object.fromEntries(rest.filter((k) => data[k]).map((k) => [k, data[k]]))} /></Expand>
    {sector && <Expand title={sector.heading || 'IT services to agent services — a conditional prediction'} testId="ai-sector-transition">
      {sector.label && <Pill tone="gray" testId="ai-sector-label">{sector.label}</Pill>}
      <div className="mt-3"><Rich value={sector} /></div>
    </Expand>}
  </div>;
};

export const EarningRoutes = ({ data }) => {
  if (!data) return null;
  const internal = (u) => u.replace(/^https?:\/\/(www\.)?learnixta\.com/, '');
  return <div className="mt-6 bg-emerald-50 border-2 border-emerald-200 rounded-3xl px-5 py-5" data-testid="ai-earning-routes">
    <p className="font-extrabold text-emerald-950 mb-2">{data.heading}</p>
    <p className="text-[15px] text-emerald-900 leading-relaxed">{data.message}</p>
    <div className="flex flex-wrap gap-3 mt-4">{(data.links || []).map((l) => <a key={l.url} href={internal(l.url)} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm px-4 py-2.5 rounded-full transition-colors" data-testid={`ai-earning-link-${internal(l.url).replace(/\W/g, '')}`}>{l.label}</a>)}</div>
  </div>;
};

export const AgentRealitySection = ({ role, Section, icon }) => role.agentRealityOpening ? (
  <Section id="agent-reality" icon={icon} title={role.agentRealityOpening.heading || 'AI agents and this career'} accent="amber" testId="ai-section-agent-reality">
    <AgentRealityOpening data={role.agentRealityOpening} guide={role.guides?.aiConceptGuide} />
  </Section>
) : null;

export const OutlookSection = ({ role, Section, icon }) => role.futureOutlook2035 ? (
  <Section id="outlook-2035" icon={icon} title={role.futureOutlook2035.heading || 'Future outlook 2035 — scenario windows'} accent="purple" testId="ai-section-outlook-2035">
    <FutureOutlook data={role.futureOutlook2035} sector={role.guides?.itSectorTransition} />
  </Section>
) : null;
